import React from 'react';
import { File, Loader2, CheckCircle2, AlertTriangle, X, Play } from 'lucide-react';

// Pretty-print a snake_case variable key. Falls back gracefully if unmapped.
const VAR_LABELS = {
    capital_adequacy:        'Capital Adequacy (CAR)',
    recapture_risk:          'Recapture Risk',
    human_capital_roti:      'Human Capital (ROTI)',
    economic_vacancy:        'Economic Vacancy',
    defect_density_pdd:      'Physical Defect Density (PDD)',
    regulatory_friction:     'Regulatory Friction',
    quality_buffer:          'Quality Buffer',
    rent_to_ami_burden:      'Rent-to-AMI Burden',
    habitability_density:    'Habitability Density',
    market_stress:           'Market Stress Index',
    tenant_stability_roti:   'Tenant Stability (ROTI)',
    oversight_credit:        'Oversight Credit',
    affordability_longevity: 'Affordability Longevity',
};
const labelFor = (key) => VAR_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/**
 * One row in a staged-file list. Status drives the icon + accent color:
 *   staged     — neutral, shows Parse + Remove
 *   parsing    — blue, spinner, no actions
 *   parsed     — green, shows detected label, Remove available
 *   rejected   — orange, shows inferred label + reasoning, Remove available
 *   error      — red, shows error message, Remove + Retry available
 */
export default function StagedFileRow({ file, status, result, error, onParse, onRemove }) {
    const sizeKb = Math.max(1, Math.round((file.size || 0) / 1024));

    let badge = null;
    let detail = null;
    let loadedVariables = null;
    let loadedBuildings = null;
    let reasoning = null;
    let recommendation = null;

    if (status === 'parsing') {
        badge = <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-medium">
            <Loader2 size={12} className="animate-spin" /> Classifying & parsing…
        </span>;
    } else if (status === 'parsed' && result?.is_high_trust) {
        const buildingNames = (result.buildings || []).map(b => b.name).filter(Boolean);
        const variableKeys = Object.keys(result.extracted_variables || {});

        badge = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-200 text-[11px] font-medium">
            <CheckCircle2 size={11} /> Detected: {result.inferred_label || result.document_type}
        </span>;

        // Always show the classifier's reasoning so the user knows WHY we
        // accepted this document.
        reasoning = result.reasoning;

        if (buildingNames.length) {
            loadedBuildings = buildingNames;
        }
        if (variableKeys.length) {
            loadedVariables = variableKeys.map(labelFor);
        }
        if (!buildingNames.length && !variableKeys.length) {
            detail = 'Recognized as a valid High-Trust document, but no buildings or variables could be extracted automatically.';
            recommendation = 'Try one of: enter the buildings manually below, upload a more detailed report (e.g., NSPIRE / REAC), or self-select the affected ratings while keeping this document on file as evidence.';
        }
    } else if (status === 'parsed' && !result?.is_high_trust) {
        badge = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-200 text-[11px] font-medium">
            <AlertTriangle size={11} /> This looks like a {result.inferred_label || 'unrecognized document'} — can't use it
        </span>;
        reasoning = result.reasoning;
        recommendation = 'For physical inspections, upload an NSPIRE, REAC, CNA, PNA, Section 504, Fire Marshal, PCR, or Engineering report. Marketing materials and tenant-side documents do not count as High-Trust.';
    } else if (status === 'error') {
        badge = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-200 text-[11px] font-medium">
            <AlertTriangle size={11} /> {error || 'Parse failed'}
        </span>;
    }

    return (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40">
            <File size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                    {/* min-w-0 lets `truncate` actually clip on overflow inside the flex item */}
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate min-w-0" title={file.name}>{file.name}</div>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 flex-shrink-0">{sizeKb} KB</span>
                </div>
                {badge && <div className="mt-1 flex items-center gap-2 flex-wrap">{badge}</div>}
                {reasoning && (
                    <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed italic">
                        “{reasoning}”
                    </div>
                )}
                {loadedBuildings && (
                    <div className="mt-1.5 flex items-start gap-1.5 flex-wrap">
                        <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">Buildings:</span>
                        {loadedBuildings.map((name, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-200 text-[10px] font-medium">
                                {name}
                            </span>
                        ))}
                    </div>
                )}
                {loadedVariables && (
                    <div className="mt-1.5 flex items-start gap-1.5 flex-wrap">
                        <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">Variables filled:</span>
                        {loadedVariables.map((label, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-200 text-[10px] font-medium">
                                {label}
                            </span>
                        ))}
                    </div>
                )}
                {detail && <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">{detail}</div>}
                {recommendation && (
                    <div className="mt-1.5 px-2 py-1.5 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 text-[11px] text-blue-800 dark:text-blue-200 leading-relaxed">
                        <span className="font-medium">Suggested next step: </span>{recommendation}
                    </div>
                )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
                {status === 'staged' && (
                    <button onClick={onParse}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-950 rounded-md transition-colors">
                        <Play size={11} /> Parse
                    </button>
                )}
                {status === 'error' && (
                    <button onClick={onParse}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-md transition-colors">
                        Retry
                    </button>
                )}
                {status !== 'parsing' && (
                    <button onClick={onRemove}
                            className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                            title="Remove">
                        <X size={14} />
                    </button>
                )}
            </div>
        </div>
    );
}
